import { FC, useState } from "react";
import { useAppSelector } from "../../app/store/store";
import { canViewShiftSection, FEATURES, FEATURE_SECTIONS, hasFeature } from "../../entities/my";
import { studentService } from "../../entities/student";
import { OpenContainer } from "../../features/openContainer/ui/OpenContainer";
import { LoaderSpinner } from "../../shared/ui/spinner";
import { SurfaceBlock } from "../../shared/ui/surfaceBlock";
import dataListClasses from "../../shared/ui/dataList/dataList.module.scss";
import { AssistantPenaltyBonus } from "../../widgets/assistantPenaltyBonus";
import { TutorAccrual } from "../../widgets/tutorAccrual";
import { TutorAvailabilityCalendar } from "../../widgets/scheduleCalendar";
import { StaffAccruals } from "../../widgets/staffAccruals";
import { StaffShiftCalendar } from "../../widgets/staffShiftCalendar";
import { StudentsWidget } from "../../widgets/students";
import { IStaffProfileSectionsContext } from "../../widgets/staff";
import { TutorTags, TutorWidget } from "../../widgets/tutor";
import { TutorFinance } from "../../widgets/tutorFinance";
import { TutorPenaltyBonus } from "../../widgets/tutorPenaltyBonus";
import { UserDocumentsWidget } from "../../widgets/documents";
import { LabelBadges } from '../../shared/ui/labels';
import { TutorLessonsCalendar } from "./calendar/TutorLessonsCalendar";
import classes from './staff.module.scss'

type TSectionIcon = 'data' | 'document' | 'calendar' | 'schedule' | 'money' | 'users'

const StaffSectionIcon: FC<{type: TSectionIcon}> = ({type}) => {
    if(type === 'document') return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3H14L19 8V21H7V3Z" /><path d="M14 3V8H19" /><path d="M10 13H16M10 17H16" /></svg>
    if(type === 'calendar') return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3V7M17 3V7M3 10H21" /></svg>
    if(type === 'schedule') return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7V12L15.5 14" /></svg>
    if(type === 'money') return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10H21M16 14H18" /></svg>
    if(type === 'users') return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3" /><path d="M3 20C3.7 16.8 5.7 15 9 15C12.3 15 14.3 16.8 15 20M17 8C19 8 20.5 9.4 20.5 11.2M17.5 15.5C19.7 16 20.9 17.4 21 20" /></svg>
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11V17M12 7H12.01" /></svg>
}

export const StaffProfileSections: FC<IStaffProfileSectionsContext> = ({
    staffUserId, showAssistantPenaltyBonus, showTutorProfile,
    isTutorProfileLoading, tutorUserId, tutorHeaderData, onTutorArchiveChange, onTutorSanctionSequenceReset, onTutorTagsChange, onWorkTgMoveSuccess, tutorDataList, isTutorStudentsMove,
    setIsTutorStudentsMove, editPath, showTutorFinance, showTutorPayouts,
    showTutorPenaltyBonus, showTutorStudents, showShiftCalendar, canResetStaffPassword, onResetStaffPassword, canMoveStaffWorkTg,
}) => {
    const {my} = useAppSelector(state => state.myReducer)
    const [financeRevision, setFinanceRevision] = useState(0)
    const canViewCalendars = hasFeature(my, FEATURE_SECTIONS.CALENDAR)
    const canViewShiftCalendar = canViewShiftSection(my)
    const isTutorReady = showTutorProfile && !isTutorProfileLoading && tutorUserId > 0
    const canViewDocuments = hasFeature(my, FEATURES.DOCUMENTS.VIEW, staffUserId)
    const canEditDocuments = canViewDocuments && hasFeature(my, FEATURES.DOCUMENTS.ADD, staffUserId)

    return (
        <section className={classes.profileSections}>
            {showAssistantPenaltyBonus && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <AssistantPenaltyBonus userId={staffUserId} titleIcon={<StaffSectionIcon type="money" />} />
                </SurfaceBlock>
            )}

            {showTutorProfile && isTutorProfileLoading && (
                <section className={classes.loader}><LoaderSpinner /></section>
            )}

            {isTutorReady && (tutorHeaderData?.labels?.length || 0) > 0 && (
                <SurfaceBlock className={`${classes.sectionBlock} ${classes.labelsWidget}`}>
                    <LabelBadges labels={tutorHeaderData?.labels} />
                </SurfaceBlock>
            )}

            {isTutorReady && (
                <SurfaceBlock className={classes.tutorHero}>
                    <TutorWidget
                        id={tutorUserId}
                        isStudentsMove={isTutorStudentsMove}
                        setIsStudentsMove={setIsTutorStudentsMove}
                        staffMode
                        headerOnly
                        editPath={editPath}
                        canResetPassword={canResetStaffPassword}
                        onResetPassword={onResetStaffPassword}
                        canMoveWorkTg={canMoveStaffWorkTg}
                        onWorkTgMoveSuccess={onWorkTgMoveSuccess}
                        prefetchedTutor={tutorHeaderData}
                        onTutorArchiveChange={onTutorArchiveChange}
                        onTutorSanctionSequenceReset={onTutorSanctionSequenceReset}
                    />
                    <section className={classes.dataOpenBlock}>
                        <OpenContainer title="Данные" icon={<StaffSectionIcon type="data" />}>
                            <section className={dataListClasses.container}>
                                <ul className={dataListClasses.list}>
                                    {tutorDataList.map((item, index) => <li key={index}>{item}</li>)}
                                </ul>
                            </section>
                        </OpenContainer>
                    </section>
                </SurfaceBlock>
            )}

            {isTutorReady && tutorHeaderData && (
                <section className={classes.sectionBlock}>
                    <TutorTags tutor={tutorHeaderData} onTagsChange={onTutorTagsChange} />
                </section>
            )}

            {canViewDocuments && <SurfaceBlock className={classes.sectionBlock}>
                <OpenContainer title="Документы сотрудника" icon={<StaffSectionIcon type="document" />} allowOverflow>
                    <section className={classes.openContent}>
                        <UserDocumentsWidget userId={staffUserId} canEdit={canEditDocuments} hideTitle />
                    </section>
                </OpenContainer>
            </SurfaceBlock>}

            {canViewCalendars && isTutorReady && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="Календарь занятий" icon={<StaffSectionIcon type="calendar" />} allowOverflow>
                        <section className={classes.openContent}>
                            {tutorHeaderData && (
                                <TutorLessonsCalendar
                                    tutor={tutorHeaderData}
                                    tutorUserId={staffUserId}
                                    hideTitle
                                />
                            )}
                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}

            {canViewCalendars && isTutorReady && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="График исполнителя" icon={<StaffSectionIcon type="schedule" />} allowOverflow>
                        <section className={classes.openContent}>
                            <TutorAvailabilityCalendar
                                userId={String(staffUserId)}
                                title="График исполнителя"
                                hideTitle
                            />
                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}

            {canViewShiftCalendar && showShiftCalendar && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="Начисления" icon={<StaffSectionIcon type="money" />} allowOverflow>
                        <section className={classes.openContent}>
                            <StaffAccruals userId={staffUserId} />
                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}

            {canViewShiftCalendar && showShiftCalendar && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="Персональный график смен" icon={<StaffSectionIcon type="schedule" />} allowOverflow>
                        <section className={classes.openContent}>
                            <StaffShiftCalendar userId={String(staffUserId)} hideTitle />
                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}

            {isTutorReady && showTutorFinance && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="Финансы" icon={<StaffSectionIcon type="money" />}>
                        <section className={classes.finance}>
                            <TutorFinance id={tutorUserId} userId={tutorUserId} revision={financeRevision} />
                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}

            {isTutorReady && showTutorPayouts && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <TutorAccrual tutorId={tutorUserId} titleIcon={<StaffSectionIcon type="money" />} />
                </SurfaceBlock>
            )}

            {isTutorReady && showTutorPenaltyBonus && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <TutorPenaltyBonus tutorId={tutorUserId} titleIcon={<StaffSectionIcon type="money" />} onChange={() => setFinanceRevision(value => value + 1)} />
                </SurfaceBlock>
            )}

            {isTutorReady && showTutorStudents && (
                <SurfaceBlock className={classes.sectionBlock}>
                    <OpenContainer title="Студенты" icon={<StaffSectionIcon type="users" />}>
                        <section className={classes.students}>
                            {isTutorStudentsMove
                                ? <section className={classes.emptyState}>Студенты были перемещены к другому исполнителю</section>
                                : <StudentsWidget request={() => studentService.getAllByTutorUserIdForAdmin(tutorUserId)} />}

                        </section>
                    </OpenContainer>
                </SurfaceBlock>
            )}
        </section>
    )
}
